import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Divider,
  CircularProgress,
  Tooltip,
  Button,
  Collapse,
  Stack,
  Paper,
  Checkbox,
} from "@mui/material";
import { alpha, Theme } from "@mui/material/styles";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  YouTube as YouTubeIcon,
  Visibility as VisibilityIcon,
  ThumbUp as ThumbUpIcon,
  Chat as ChatIcon,
  TrendingUp as TrendingUpIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Topic as TopicIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { contentTaxonomyTopicsPath } from "../constants/reviewRoutePaths";
import { isMentionReviewReady, isRejectedMention } from "./EpisodeMentionReviewDialog";
import { ContentItem, BrandMention } from "../types";
import BrandMentionRow from "./BrandMentionRow";
import EpisodeMetricsPanel from "./EpisodeMetricsPanel";
import { contentIdForApi } from "../hooks/usePageBrandMentions";
import { apiService } from "../services/api";

interface ReviewEpisodeSectionProps {
  contentItem: ContentItem;
  isExpanded: boolean;
  onToggle: () => void;
  brandMentions: BrandMention[];
  mentionsLoading: boolean;
  selectedBrandName: string | null;
  formatTime: (seconds: number) => string;
  getClassificationColor: (classification: string) => string;
  createYouTubeUrlWithTimestamp: (url: string, startTime: number) => string;
  formatChileanDate: (dateString: string) => string;
  getSourceIcon: (source: string) => React.ReactElement;
  onCopyContentId: (contentId: string) => void;
  onDeleteBrandMention: (mentionId: string) => Promise<void>;
  onDeleteAllMentions: (contentId: string) => Promise<void>;
  onCopyMentionId: (mentionId: string) => void;
  onEditMention: (mention: BrandMention) => void;
  onDuplicateMention: (mention: BrandMention) => void;
  onMentionDeleted?: (contentId: string, mentionId: string) => void;
  onMentionFeedback?: (
    contentId: string,
    mentionId: string,
    status: "rejected" | null,
    reason?: string,
    context?: string,
  ) => void;
  onMentionClassificationChange?: (
    contentId: string,
    mentionId: string,
    mentionType?: string,
    placementType?: string,
    visualCue?: BrandMention["visualCue"],
  ) => void;
  onEditProgram?: (
    contentId: string,
    contentTitle: string,
    currentProgramId: string | undefined,
  ) => void;
  onMentionsChanged?: () => void;
  onReviewReadyChange?: (
    contentId: string,
    ready: boolean,
  ) => Promise<{ reviewReadyAt: string | null } | void>;
  onReviewEpisodeMentions?: (contentItem: ContentItem) => void;
  forcedMentionUpdates?: ReadonlyMap<string, BrandMention>;
  /** When `onSelectChange` is set, a selection checkbox is shown in the header. */
  selected?: boolean;
  onSelectChange?: (contentId: string, selected: boolean) => void;
}

const mentionsSectionBg = (theme: Theme) =>
  theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.04)
    : alpha(theme.palette.common.black, 0.025);

function normalizeReviewReadyAt(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (typeof value === "string") return value;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object" && value !== null && "$date" in value) {
    const dateValue = (value as { $date: string }).$date;
    return typeof dateValue === "string" ? dateValue : undefined;
  }
  return undefined;
}

const ReviewEpisodeSection: React.FC<ReviewEpisodeSectionProps> = ({
  contentItem,
  isExpanded,
  onToggle,
  brandMentions,
  mentionsLoading,
  selectedBrandName,
  formatTime,
  getClassificationColor,
  createYouTubeUrlWithTimestamp,
  formatChileanDate,
  getSourceIcon,
  onCopyContentId,
  onDeleteBrandMention,
  onDeleteAllMentions,
  onCopyMentionId,
  onEditMention,
  onDuplicateMention,
  onMentionDeleted,
  onMentionFeedback,
  onMentionClassificationChange,
  onEditProgram,
  onMentionsChanged,
  onReviewReadyChange,
  onReviewEpisodeMentions,
  forcedMentionUpdates,
  selected = false,
  onSelectChange,
}) => {
  const [searchParams] = useSearchParams();
  const showMentionTranscript = searchParams.get("showMentionTranscript") === "1";
  const contentIdStr = contentIdForApi(contentItem._id);
  const [removedMentionIds, setRemovedMentionIds] = useState<Set<string>>(
    new Set(),
  );
  const [mentionOverrides, setMentionOverrides] = useState<
    Map<string, BrandMention>
  >(new Map());
  const [isDeletingMention, setIsDeletingMention] = useState<string | null>(
    null,
  );
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [isDownloadingTranscript, setIsDownloadingTranscript] =
    useState(false);
  const [reviewReadyAt, setReviewReadyAt] = useState<string | undefined>(
    normalizeReviewReadyAt(contentItem.reviewReadyAt),
  );
  const [reviewReadySaving, setReviewReadySaving] = useState(false);

  React.useEffect(() => {
    if (reviewReadySaving) return;
    setReviewReadyAt(normalizeReviewReadyAt(contentItem.reviewReadyAt));
  }, [contentItem.reviewReadyAt, reviewReadySaving]);

  const handleDownloadTranscript = async () => {
    setIsDownloadingTranscript(true);
    try {
      await apiService.downloadContentTranscript(contentIdStr);
    } catch (error) {
      console.error("Failed to download transcript:", error);
    } finally {
      setIsDownloadingTranscript(false);
    }
  };

  const mentionIdsKey = brandMentions.map((m) => m._id).join(",");
  React.useEffect(() => {
    setRemovedMentionIds(new Set());
    setMentionOverrides(new Map());
  }, [mentionIdsKey]);

  React.useEffect(() => {
    if (!forcedMentionUpdates || forcedMentionUpdates.size === 0) return;
    setMentionOverrides((prev) => {
      let changed = false;
      const next = new Map(prev);
      forcedMentionUpdates.forEach((_mention, id) => {
        if (next.delete(id)) changed = true;
      });
      return changed ? next : prev;
    });
  }, [forcedMentionUpdates]);

  const displayMentions = React.useMemo(
    () =>
      brandMentions
        .filter((m) => !removedMentionIds.has(m._id))
        .map(
          (m) =>
            forcedMentionUpdates?.get(m._id) ??
            mentionOverrides.get(m._id) ??
            m,
        )
        .sort((a, b) => a.startTime - b.startTime),
    [brandMentions, removedMentionIds, mentionOverrides, forcedMentionUpdates],
  );

  const mentionReadyCount = displayMentions.filter(
    (m) => !isRejectedMention(m) && isMentionReviewReady(m),
  ).length;
  const reviewableMentionCount = displayMentions.filter(
    (m) => !isRejectedMention(m),
  ).length;

  const showMentionsBlock = isExpanded;
  const hasMetrics =
    contentItem.dailyMetrics && contentItem.dailyMetrics.length > 0;

  const latestMetric =
    hasMetrics && contentItem.dailyMetrics
      ? [...contentItem.dailyMetrics].sort(
          (a, b) =>
            new Date(b.collectedAt).getTime() -
            new Date(a.collectedAt).getTime(),
        )[0]
      : null;

  const handleDeleteBrandMention = async (mentionId: string) => {
    setIsDeletingMention(mentionId);
    try {
      await onDeleteBrandMention(mentionId);
      setRemovedMentionIds((prev) => new Set(prev).add(mentionId));
      onMentionDeleted?.(contentItem._id, mentionId);
      onMentionsChanged?.();
    } catch (error) {
      console.error("Failed to delete brand mention:", error);
    } finally {
      setIsDeletingMention(null);
    }
  };

  const handleDeleteAllMentions = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete ALL brand mentions for this episode? This will also remove the analyzed status and cannot be undone.",
      )
    ) {
      try {
        await onDeleteAllMentions(contentItem._id);
        setRemovedMentionIds(
          new Set(brandMentions.map((mention) => mention._id)),
        );
        onMentionsChanged?.();
      } catch (error) {
        console.error("Failed to delete all brand mentions:", error);
      }
    }
  };

  const handleMentionFeedback = (
    mentionId: string,
    status: "rejected" | null,
    reason?: string,
    context?: string,
  ) => {
    setMentionOverrides((prev) => {
      const next = new Map(prev);
      const base =
        next.get(mentionId) ??
        brandMentions.find((m) => m._id === mentionId);
      if (base) {
        next.set(mentionId, {
          ...base,
          feedback: status
            ? { status, reason, ...(context ? { context } : {}) }
            : null,
        });
      }
      return next;
    });
    onMentionFeedback?.(contentItem._id, mentionId, status, reason, context);
  };

  const handleMentionClassificationChange = (
    mentionId: string,
    mentionType?: string,
    placementType?: string,
    visualCue?: BrandMention["visualCue"],
  ) => {
    setMentionOverrides((prev) => {
      const next = new Map(prev);
      const base =
        next.get(mentionId) ??
        brandMentions.find((m) => m._id === mentionId);
      if (base) {
        next.set(mentionId, {
          ...base,
          ...(mentionType ? { classification: mentionType } : {}),
          ...(placementType !== undefined
            ? {
                placementType:
                  placementType as BrandMention["placementType"],
              }
            : {}),
          ...(visualCue !== undefined ? { visualCue } : {}),
        });
      }
      return next;
    });
    onMentionClassificationChange?.(
      contentItem._id,
      mentionId,
      mentionType,
      placementType,
      visualCue,
    );
  };

  const handleProgramEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEditProgram?.(
      contentItem._id,
      contentItem.title,
      contentItem.program?._id,
    );
  };

  const handleReviewReadyToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onReviewReadyChange || reviewReadySaving) return;
    const nextReady = !reviewReadyAt;
    setReviewReadySaving(true);
    setReviewReadyAt(nextReady ? new Date().toISOString() : undefined);
    try {
      const result = await onReviewReadyChange(contentIdStr, nextReady);
      if (result?.reviewReadyAt !== undefined) {
        setReviewReadyAt(normalizeReviewReadyAt(result.reviewReadyAt));
      }
    } catch {
      setReviewReadyAt(normalizeReviewReadyAt(contentItem.reviewReadyAt));
    } finally {
      setReviewReadySaving(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        scrollMarginTop: 16,
        borderColor: selected
          ? "primary.main"
          : isExpanded
            ? "primary.light"
            : "divider",
        transition: "border-color 0.15s",
      }}
      data-content-id={contentIdStr}
    >
      <Box sx={{ p: 2 }}>
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          gap={2}
        >
            {onSelectChange && (
              <Checkbox
                size="small"
                checked={selected}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onSelectChange(contentIdStr, e.target.checked)}
                inputProps={{ "aria-label": `Select ${contentItem.title}` }}
                sx={{ p: 0.5, mt: -0.25, ml: -0.75, mr: -1 }}
              />
            )}
            <Box flex={1} minWidth={0}>
              <Box mb={1}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography
                    variant="subtitle1"
                    sx={(theme) => ({
                      fontFamily: theme.typography.body1.fontFamily,
                      fontWeight: 600,
                    })}
                  >
                    {contentItem.title}
                  </Typography>
                  {contentItem.program && contentItem.program._id && (
                    <Chip
                      label={contentItem.program.name}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      onDelete={handleProgramEdit}
                      deleteIcon={<CloseIcon fontSize="small" />}
                      sx={{
                        fontSize: "0.75rem",
                        height: "24px",
                        fontWeight: "bold",
                        "& .MuiChip-deleteIcon": {
                          fontSize: "14px",
                          margin: "0 2px 0 -4px",
                        },
                      }}
                      title="Click to edit program"
                    />
                  )}
                  {latestMetric && !metricsOpen && (
                    <Chip
                      icon={<TrendingUpIcon sx={{ fontSize: "14px !important" }} />}
                      label={`${latestMetric.views.toLocaleString()} views`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.7rem", height: "22px" }}
                    />
                  )}
                </Box>
                {(contentItem.isTranscribed || contentItem.isAnalyzed || reviewReadyAt) && (
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mt={0.75}>
                    {reviewReadyAt && (
                      <Chip
                        label="Ready"
                        size="small"
                        color="success"
                        variant="filled"
                        sx={{ fontSize: "0.75rem", height: "24px", fontWeight: "bold" }}
                      />
                    )}
                    {contentItem.isTranscribed && (
                      <Chip
                        label="Transcribed"
                        size="small"
                        color="success"
                        variant="filled"
                        sx={{ fontSize: "0.75rem", height: "24px", fontWeight: "bold" }}
                      />
                    )}
                    {contentItem.isAnalyzed && (
                      <Chip
                        label={
                          contentItem.lastAnalyzedAt
                            ? `Analyzed ${formatChileanDate(contentItem.lastAnalyzedAt)}`
                            : "Analyzed"
                        }
                        size="small"
                        color="info"
                        variant="filled"
                        sx={{ fontSize: "0.75rem", height: "24px", fontWeight: "bold" }}
                      />
                    )}
                  </Box>
                )}
              </Box>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                {getSourceIcon(contentItem.source)}
                <Typography variant="caption" color="text.secondary">
                  {contentItem.publishedAt &&
                    formatChileanDate(contentItem.publishedAt)}
                  {contentItem.publishedAt && " • "}
                  {contentItem.mediaType}
                </Typography>
                {contentItem.duration && (
                  <Typography variant="caption" color="text.secondary">
                    {" • "}
                    {formatTime(contentItem.duration)}
                  </Typography>
                )}
                {contentItem.isAnalyzed && (
                  <Typography variant="caption" color="primary" fontWeight="bold">
                    {" • "}
                    {selectedBrandName
                      ? `${displayMentions.length} matching mentions`
                      : `${contentItem.brandMentionsCount || displayMentions.length} brand mentions`}
                  </Typography>
                )}
                {contentItem.source === "youtube" && (
                  <>
                    {contentItem.views !== undefined && contentItem.views > 0 && (
                      <Tooltip title="Views">
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <VisibilityIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                          <Typography variant="caption" color="text.secondary">
                            {contentItem.views.toLocaleString()}
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}
                    {contentItem.likes !== undefined && contentItem.likes > 0 && (
                      <Tooltip title="Likes">
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <ThumbUpIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                          <Typography variant="caption" color="text.secondary">
                            {contentItem.likes.toLocaleString()}
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}
                    {contentItem.comments !== undefined &&
                      contentItem.comments > 0 && (
                        <Tooltip title="Comments">
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <ChatIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                            <Typography variant="caption" color="text.secondary">
                              {contentItem.comments.toLocaleString()}
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                  </>
                )}
              </Box>
            </Box>

            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              flexShrink={0}
              flexWrap="wrap"
              justifyContent="flex-end"
            >
              {onReviewReadyChange && (
                <Button
                  size="small"
                  variant={reviewReadyAt ? "outlined" : "contained"}
                  color={reviewReadyAt ? "inherit" : "success"}
                  disabled={reviewReadySaving}
                  onClick={handleReviewReadyToggle}
                >
                  {reviewReadySaving
                    ? "Saving..."
                    : reviewReadyAt
                      ? "Mark pending"
                      : "Mark ready"}
                </Button>
              )}
              {hasMetrics && (
                <Button
                  size="small"
                  variant={metricsOpen ? "contained" : "outlined"}
                  startIcon={<TrendingUpIcon />}
                  onClick={() => setMetricsOpen((open) => !open)}
                >
                  Metrics
                </Button>
              )}
              {contentItem.isAnalyzed && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={handleDeleteAllMentions}
                >
                  Delete all
                </Button>
              )}
              {contentItem.isTranscribed && (
                <Tooltip title="Download S3 transcript">
                  <span>
                    <IconButton
                      onClick={handleDownloadTranscript}
                      size="small"
                      color="primary"
                      disabled={isDownloadingTranscript}
                    >
                      {isDownloadingTranscript ? (
                        <CircularProgress size={20} />
                      ) : (
                        <DownloadIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {contentItem.source === "youtube" && contentItem.url && (
                <Tooltip title="Open on YouTube">
                  <IconButton
                    onClick={() => window.open(contentItem.url, "_blank")}
                    size="small"
                    color="primary"
                  >
                    <YouTubeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Button
                size="small"
                variant="outlined"
                component={RouterLink}
                to={contentTaxonomyTopicsPath(contentIdStr)}
                startIcon={<TopicIcon fontSize="small" />}
                onClick={(e) => e.stopPropagation()}
              >
                Topics
              </Button>
              <IconButton
                onClick={() => onCopyContentId(contentItem._id)}
                size="small"
                title="Copy Content ID"
              >
                <CopyIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={onToggle} size="small" title={isExpanded ? "Collapse mentions" : "Expand mentions"}>
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>

        <Collapse in={metricsOpen}>
          <Box sx={{ mt: 2 }}>
            <EpisodeMetricsPanel contentItem={contentItem} />
          </Box>
        </Collapse>
      </Box>

      {showMentionsBlock && (
        <Box
          sx={(theme) => ({
            px: 2,
            py: 1.5,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: mentionsSectionBg(theme),
          })}
        >
            {mentionsLoading && displayMentions.length === 0 ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            ) : displayMentions.length > 0 ? (
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  flexWrap="wrap"
                  sx={{ mb: 1 }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="medium"
                    color="text.secondary"
                  >
                    Mentions ({reviewableMentionCount})
                    {mentionReadyCount > 0 &&
                      ` · ${mentionReadyCount} / ${reviewableMentionCount} ready`}
                    {selectedBrandName && ` · ${selectedBrandName}`}
                  </Typography>
                  {onReviewEpisodeMentions && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<EditIcon />}
                      onClick={() => onReviewEpisodeMentions(contentItem)}
                    >
                      Review mentions
                    </Button>
                  )}
                </Stack>
                <Stack spacing={0} divider={<Divider flexItem />}>
                  {displayMentions.map((mention) => (
                    <BrandMentionRow
                      key={mention._id}
                      mention={mention}
                      contentUrl={contentItem.url}
                      showMentionTranscript={showMentionTranscript}
                      onDelete={handleDeleteBrandMention}
                      onEdit={onEditMention}
                      onDuplicate={onDuplicateMention}
                      onCopyMentionId={onCopyMentionId}
                      onFeedback={handleMentionFeedback}
                      onClassificationChange={handleMentionClassificationChange}
                      formatTime={formatTime}
                      getClassificationColor={getClassificationColor}
                      createYouTubeUrlWithTimestamp={createYouTubeUrlWithTimestamp}
                      isDeleting={isDeletingMention === mention._id}
                    />
                  ))}
                </Stack>
              </Box>
            ) : (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                flexWrap="wrap"
              >
                <Typography variant="body2" color="text.secondary">
                  {selectedBrandName
                    ? `No mentions for "${selectedBrandName}" in this episode.`
                    : contentItem.isAnalyzed
                      ? "No brand mentions found for this content item."
                      : "No brand mentions yet."}
                </Typography>
                {onReviewEpisodeMentions && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => onReviewEpisodeMentions(contentItem)}
                  >
                    Review mentions
                  </Button>
                )}
              </Stack>
            )}
        </Box>
      )}
    </Paper>
  );
};

export default ReviewEpisodeSection;
